/**
 * Global error handler for unhandled exceptions
 */

// Global error handler for unhandled promise rejections
export const setupGlobalErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    console.error('Unhandled promise rejection:', event.reason);

    // Handle specific clipboard permission errors silently
    if (
      event.reason instanceof DOMException &&
      (event.reason.name === 'NotAllowedError' ||
        event.reason.name === 'SecurityError')
    ) {
      console.warn(
        'Clipboard operation blocked by browser security policy. Fallback should be used.'
      );
      event.preventDefault(); // Prevent the default error logging
      return;
    }

    // Handle other types of errors as needed
    if (event.reason?.message?.includes('clipboard')) {
      console.warn('Clipboard operation failed:', event.reason.message);
      event.preventDefault();
      return;
    }
  });

  // Handle uncaught JavaScript errors
  window.addEventListener('error', event => {
    console.error('Uncaught error:', event.error);

    // Handle clipboard-related errors
    if (
      event.error?.message?.includes('clipboard') ||
      event.error?.message?.includes('NotAllowedError')
    ) {
      console.warn('Clipboard operation error caught:', event.error.message);
      event.preventDefault();
      return;
    }
  });
};

/**
 * Safe async function wrapper that handles clipboard errors gracefully
 */
export const safeAsync = async <T>(
  asyncFn: () => Promise<T>,
  fallbackFn?: () => T | Promise<T>
): Promise<T | undefined> => {
  try {
    return await asyncFn();
  } catch (error) {
    if (
      error instanceof DOMException &&
      (error.name === 'NotAllowedError' || error.name === 'SecurityError')
    ) {
      console.warn('Permission denied, using fallback if available');
      if (fallbackFn) {
        try {
          return await fallbackFn();
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
        }
      }
    } else {
      console.error('Async operation failed:', error);
    }
    return undefined;
  }
};
